import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ENTITY_STYLE = {
  universe: "bg-indigo-50 text-indigo-700", contacts: "bg-emerald-50 text-emerald-700",
  leads: "bg-amber-50 text-amber-700", opportunities: "bg-sky-50 text-sky-700",
};

export function ImportHistory() {
  const [expanded, setExpanded] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["import-logs"],
    queryFn: async () => (await api.get("/master-data/import-logs")).data,
  });
  if (isLoading) return <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const rows = data?.rows || [];
  return (
    <Card className="p-5" data-testid="import-history">
      <p className="font-heading font-semibold text-slate-900 mb-1">Import History</p>
      <p className="text-xs text-slate-500 mb-4">Every Excel import across Universe, Contacts, Leads and Opportunities — who, when and what changed.</p>
      {rows.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No imports logged yet.</p> : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["When", "Entity", "Mode", "File", "By", "Created", "Updated", "Deleted", "Skipped", "Errors"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
              ))}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className={r.errors_count ? "cursor-pointer" : ""} onClick={() => r.errors_count && setExpanded(expanded === r.id ? null : r.id)} data-testid={`import-log-${r.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDate(r.at)}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold capitalize ${ENTITY_STYLE[r.entity] || "bg-slate-100 text-slate-600"}`}>{r.entity}</span></TableCell>
                  <TableCell className="text-xs text-slate-600">{(r.mode || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[180px] truncate" title={r.filename}>{r.filename || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{r.by_name}</TableCell>
                  <TableCell className="font-mono text-sm font-semibold text-emerald-700">{r.created || 0}</TableCell>
                  <TableCell className="font-mono text-sm">{r.updated || 0}</TableCell>
                  <TableCell className="font-mono text-sm text-red-600">{r.deleted || 0}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{r.skipped || 0}</TableCell>
                  <TableCell className="font-mono text-sm">{r.errors_count ? <span className="text-red-600 font-semibold">{r.errors_count} ▾</span> : 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {expanded && (() => {
        const r = rows.find((x) => x.id === expanded);
        return r?.errors_sample?.length ? (
          <div className="mt-3 rounded-md bg-red-50 border border-red-100 p-3 text-xs text-red-700 space-y-0.5" data-testid="import-log-errors">
            {r.errors_sample.map((e, i) => <p key={i}>Row {e.row}: {e.error}</p>)}
          </div>
        ) : null;
      })()}
    </Card>
  );
}
